import { useRef } from "react";
import classNames from "classnames";
import { useQueryClient } from "react-query";
import { COMMENTS_BASE_QUERY_KEY } from "@/react/components/CommentsV3/CommentsList/getCommentsListKey";
import { usePostResource } from "@/react/hooks/posts/usePostResource";
import type { ImagePost } from "@/react/types";
import {
  ModalManager,
  useModal,
} from "@circle-react/components/Layout/ModalManagerProvider";
import {
  CurrentPostContextProvider,
  CurrentSpaceContextProvider,
} from "@circle-react/contexts";
import { PostsDetailsContextProvider } from "@circle-react/contexts/postsDetailsContext";
import { Modal } from "@circle-react-uikit/ModalV2";
import { Content } from "./Content";

interface FullPostModalComponentProps {
  autofocusReplyForm?: boolean;
  isSharedPostView?: boolean;
  onPostChange: (post: ImagePost) => void;
  onPostDestroy: (postId: number) => void;
  post?: ImagePost;
  spaceId?: number;
  startingImageIndex?: number;
}
export const FullPostModalComponent = ({
  post,
  spaceId,
  onPostChange,
  onPostDestroy,
  startingImageIndex,
  autofocusReplyForm = false,
  isSharedPostView = false,
}: FullPostModalComponentProps) => {
  const modal = useModal();
  const nullInitialFocusRef = useRef(null);
  const { post: postResource, onPostChange: onPostResourceChange } =
    usePostResource({
      spaceId: post?.space_id,
      spaceSlug: post?.space_slug,
      postSlug: post?.slug,
    });

  const queryClient = useQueryClient();

  return (
    <CurrentSpaceContextProvider spaceId={spaceId}>
      <PostsDetailsContextProvider
        perPagePostIds={[[post?.id]]}
        isSharedPostView={isSharedPostView}
      >
        <CurrentPostContextProvider
          post={postResource || post}
          usedIn="full-post-modal"
        >
          <Modal
            initialFocus={nullInitialFocusRef}
            isOpen={modal.visible}
            onClose={() => {
              void queryClient.invalidateQueries([
                COMMENTS_BASE_QUERY_KEY,
                post?.id,
              ]);
              modal.remove();
            }}
            contentPortalId="full-post-modal-portal"
          >
            <Modal.Overlay />
            <Modal.Content
              size="full"
              className={classNames(
                "flex !flex-row flex-wrap content-start sm:!max-h-[836px] sm:!max-w-[1256px]",
              )}
            >
              {/* This is a hack to prevent the modal from focusing the ImageOptions right away */}
              <div ref={nullInitialFocusRef} tabIndex={-1} />
              <Content
                onPostChange={(post, options) => {
                  void onPostChange(post); // this updates the infinite query list
                  void onPostResourceChange(post, options); // this updates the post resource
                }}
                onPostDestroy={onPostDestroy}
                startingImageIndex={startingImageIndex}
                autofocusReplyForm={autofocusReplyForm}
              />
            </Modal.Content>
          </Modal>
        </CurrentPostContextProvider>
      </PostsDetailsContextProvider>
    </CurrentSpaceContextProvider>
  );
};
const FullPostModal = ModalManager.create(
  (props: FullPostModalComponentProps) => <FullPostModalComponent {...props} />,
);
export const useFullPostModal = () => useModal(FullPostModal);
